import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import type { AreaType } from "@ippon/shared-types";
import { toApiErrorPayload } from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

export const createRepair = createAsyncThunk<
  any,
  {
    property_id: string;
    repair_title: string;
    area_type: AreaType;
    area_name?: string;
    status: string;
    category: string[];
    repair_images?: any[];
    estimate_images?: any[];
  }
>("repairs/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/repairs`, payload);
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to create repair");
  }
});

export const fetchRepairsByProperty = createAsyncThunk<
  any,
  {
    property_id: string;
    area_type?: AreaType;
    area_name?: string;
    page?: number;
    limit?: number;
  }
>(
  "repairs/fetchByProperty",
  async (
    { property_id, area_type, area_name, page, limit },
    { rejectWithValue }
  ) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs`, {
        params: {
          property_id,
          ...(area_type ? { area_type } : {}),
          ...(area_name ? { area_name } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
        },
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(toApiErrorPayload(error, "Failed to fetch repairs"));
    }
  }
);

export const fetchRepairsByClient = createAsyncThunk<
  any,
  {
    client_id: string;
    page?: number;
    limit?: number;
    paginate_by?: "repair" | "estimate";
  }
>(
  "repairs/fetchByClient",
  async (
    { client_id, page = 1, limit = 10, paginate_by },
    { rejectWithValue }
  ) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs`, {
        params: {
          client_id,
          page,
          limit,
          ...(paginate_by ? { paginate_by } : {}),
        },
      });
      return res.data.data;
    } catch (error: unknown) {
      return rejectWithValue(
        toApiErrorPayload(error, "Failed to fetch repairs by client")
      );
    }
  }
);

export const fetchEstimatesByClient = createAsyncThunk<
  any,
  {
    client_id: string;
    page?: number;
    limit?: number;
  }
>(
  "repairs/fetchEstimatesByClient",
  async ({ client_id, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs`, {
        params: {
          client_id,
          page,
          limit,
          paginate_by: "estimate",
        },
      });
      return res.data.data;
    } catch (error: unknown) {
      return rejectWithValue(
        toApiErrorPayload(error, "Failed to fetch estimates by client")
      );
    }
  }
);

export const fetchRepairById = createAsyncThunk<any, { repair_id: string }>(
  "repairs/fetchById",
  async ({ repair_id }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/repairs/${repair_id}`);
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch repair by id"
      );
    }
  }
);

// Update repair (partial). Supports appending estimate_images and/or repair_images
export const updateRepair = createAsyncThunk<
  any,
  {
    repair_id: string;
    // basic fields
    repair_title?: string;
    area_type?: AreaType;
    area_name?: string;
    status?: string;
    category?: string | string[];
    repair_images?: any[];
    estimate_images?: any[];
    remove_repair_images?: string[];
  }
>("repairs/update", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, ...body } = payload as any;
    const res = await ipponApi.put(
      `${BaseURL}/api/v1/repairs/${repair_id}`,
      body
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to update repair");
  }
});

export const uploadRepairEstimate = createAsyncThunk<
  { fileId: string },
  {
    repair_id: string;
    estimate_image: { data: string; originalFileName?: string; mimeType?: string };
  }
>("repairs/uploadEstimate", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, estimate_image } = payload;
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/upload`,
      { estimate_image }
    );
    const data = res.data?.data ?? res.data;
    const fileId = data?.fileId;
    if (!fileId) {
      return rejectWithValue("Missing fileId in response");
    }
    return { fileId };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to upload repair estimate"
    );
  }
});

export const analyzeRepairEstimates = createAsyncThunk<
  any,
  {
    repair_id: string;
    estimate_images: any[];
  }
>("repairs/analyzeRepairEstimates", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, estimate_images } = payload;
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/analyze`,
      { estimate_images }
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to analyze repair estimate"
    );
  }
});

// Analyze existing S3 estimates without new uploads
export const analyzeExistingRepairEstimates = createAsyncThunk<
  any,
  { repair_id: string; estimate_id?: string; estimate_ids?: string[] }
>("repairs/analyzeExisting", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, estimate_id, estimate_ids } = payload;
    const body = estimate_ids
      ? { estimate_ids }
      : estimate_id
        ? { estimate_id }
        : undefined;
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/analyze-existing`,
      body
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to analyze existing repair estimates"
    );
  }
});

export const deleteRepairEstimate = createAsyncThunk<
  any,
  {
    repair_id: string;
    repair_estimate_id: string;
  }
>("repairs/deleteRepairEstimate", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, repair_estimate_id } = payload;
    const res = await ipponApi.delete(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/${repair_estimate_id}`
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to delete repair estimate"
    );
  }
});

// Update a single estimate status under a repair
export const updateRepairEstimateStatus = createAsyncThunk<
  any,
  {
    repair_id: string;
    repair_estimate_id: string;
    status?: string;
    estimate_company_name?: string;
    estimate_amount?: number;
  }
>("repairs/updateEstimateStatus", async (payload, { rejectWithValue }) => {
  try {
    const {
      repair_id,
      repair_estimate_id,
      status,
      estimate_company_name,
      estimate_amount,
    } = payload;
    const res = await ipponApi.patch(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/${repair_estimate_id}`,
      { status, estimate_company_name, estimate_amount }
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to update estimate status"
    );
  }
});

/**
 * 見積選定 (= 採用) + 支出連携
 * - 紐付き支出無し + create_expense=true → 支出新規作成
 * - 紐付き支出無し + create_expense=false → 選定フラグのみ
 * - 紐付き支出あり → 既存支出のステータスを支払い済みに更新
 */
export const selectRepairEstimate = createAsyncThunk<
  any,
  {
    repair_id: string;
    repair_estimate_id: string;
    create_expense: boolean;
  }
>("repairs/selectEstimate", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, repair_estimate_id, create_expense } = payload;
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/repairs/${repair_id}/estimates/${repair_estimate_id}/select`,
      { create_expense }
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message ||
        error.response?.data ||
        "見積選定に失敗しました"
    );
  }
});

export const patchRepairStatus = createAsyncThunk<
  { repair_id: string; status: string },
  { repair_id: string; status: string }
>("repairs/patchStatus", async (payload, { rejectWithValue }) => {
  try {
    const { repair_id, status } = payload;
    await ipponApi.patch(`${BaseURL}/api/v1/repairs/${repair_id}/status`, {
      status,
    });
    return { repair_id, status };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to patch repair status"
    );
  }
});

export const deleteRepair = createAsyncThunk<any, { repair_id: string }>(
  "repairs/delete",
  async (payload, { rejectWithValue }) => {
    try {
      console.log(`🗑️ Deleting repair: ${payload.repair_id}`);
      const res = await ipponApi.delete(
        `${BaseURL}/api/v1/repairs/${payload.repair_id}`
      );
      console.log(`✅ Repair deleted successfully:`, res.data);
      return res.data.data;
    } catch (error: any) {
      console.error("Error message:", error.message);
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to delete repair",
        status: error.response?.status,
        repair_id: payload.repair_id,
        originalError: error.response?.data || error.message,
      });
    }
  }
);

type RepairsState = {
  creating: {
    loading: boolean;
    error: boolean;
    data: any;
  };
  list: {
    loading: boolean;
    error: boolean | unknown;
    data: any[];
    total: number;
    page: number;
    limit: number;
    loaded: boolean;
  };
  estimateList: {
    loading: boolean;
    error: boolean | unknown;
    data: any[];
    total: number;
    page: number;
    limit: number;
    loaded: boolean;
  };
  current?: {
    loading: boolean;
    error: boolean;
    data: any | null;
  };
};

const initialState: RepairsState = {
  creating: {
    loading: false,
    error: false,
    data: null,
  },
  list: {
    loading: false,
    error: false,
    data: [],
    total: 0,
    page: 1,
    limit: 10,
    loaded: false,
  },
  estimateList: {
    loading: false,
    error: false,
    data: [],
    total: 0,
    page: 1,
    limit: 10,
    loaded: false,
  },
  current: {
    loading: false,
    error: false,
    data: null,
  },
};

const updateEstimateRows = (
  rows: any[],
  repairId: string,
  repairEstimateId: string,
  estimate: any
) =>
  rows.map((r: any) => {
    if (r.repair_id !== repairId) return r;
    const estimates = Array.isArray(r.repair_estimates)
      ? r.repair_estimates
      : [];
    return {
      ...r,
      repair_estimates: estimates.map((e: any) =>
        String(e.repair_estimate_id) === String(repairEstimateId)
          ? { ...e, ...estimate }
          : e
      ),
    };
  });

export const repairsSlice = createSlice({
  name: "repairs",
  initialState,
  reducers: {
    updateRepairStatusInList(
      state,
      action: { payload: { repair_id: string; status: string } }
    ) {
      const { repair_id, status } = action.payload;
      const item = state.list.data.find(
        (r: any) => r.repair_id === repair_id
      );
      if (item) {
        (item as any).status = status;
      }
    },
    updateEstimateInList(
      state,
      action: {
        payload: {
          repair_id: string;
          repair_estimate_id: string;
          estimate: any;
        };
      }
    ) {
      const { repair_id, repair_estimate_id, estimate } = action.payload;
      state.list.data = updateEstimateRows(
        state.list.data,
        repair_id,
        repair_estimate_id,
        estimate
      );
      state.estimateList.data = updateEstimateRows(
        state.estimateList.data,
        repair_id,
        repair_estimate_id,
        estimate
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createRepair.pending, (state) => {
      state.creating.loading = true;
      state.creating.error = false;
    });
    builder.addCase(createRepair.fulfilled, (state, action) => {
      state.creating.loading = false;
      state.creating.data = action.payload;
    });
    builder.addCase(createRepair.rejected, (state) => {
      state.creating.loading = false;
      state.creating.error = true;
    });

    builder.addCase(fetchRepairsByProperty.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(fetchRepairsByProperty.fulfilled, (state, action) => {
      state.list.loading = false;
      const payload = action.payload;
      // バックエンドから { total, page, limit, repairs } または { total, page, limit, items } の形式で返る
      const data = payload?.items ?? payload?.repairs;
      if (data !== undefined) {
        state.list.data = Array.isArray(data) ? data : [];
        state.list.total = Number(payload?.total || 0);
        state.list.page = Number(payload?.page || 1);
        state.list.limit = Number(payload?.limit || state.list.limit || 10);
      }
      state.list.loaded = true;
    });
    builder.addCase(fetchRepairsByProperty.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });

    builder.addCase(fetchRepairsByClient.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(fetchRepairsByClient.fulfilled, (state, action) => {
      state.list.loading = false;
      const payload = action.payload;
      const repairs = Array.isArray(payload?.repairs) ? payload.repairs : [];
      state.list.data = repairs;
      state.list.total = Number(payload?.total || repairs.length || 0);
      state.list.page = Number(payload?.page || 1);
      state.list.limit = Number(payload?.limit || state.list.limit || 10);
      state.list.loaded = true;
    });
    builder.addCase(fetchRepairsByClient.rejected, (state, action) => {
      state.list.loading = false;
      state.list.error = action.payload || true;
    });

    builder.addCase(fetchEstimatesByClient.pending, (state) => {
      state.estimateList.loading = true;
      state.estimateList.error = false;
    });
    builder.addCase(fetchEstimatesByClient.fulfilled, (state, action) => {
      state.estimateList.loading = false;
      const payload = action.payload;
      const repairs = Array.isArray(payload?.repairs) ? payload.repairs : [];
      state.estimateList.data = repairs;
      state.estimateList.total = Number(payload?.total || repairs.length || 0);
      state.estimateList.page = Number(payload?.page || 1);
      state.estimateList.limit = Number(
        payload?.limit || state.estimateList.limit || 10
      );
      state.estimateList.loaded = true;
    });
    builder.addCase(fetchEstimatesByClient.rejected, (state, action) => {
      state.estimateList.loading = false;
      state.estimateList.error = action.payload || true;
    });

    // fetch by id
    builder.addCase(fetchRepairById.pending, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      const nextId = (action.meta.arg as any)?.repair_id || null;
      const cur: any = state.current;
      // 別 repair への遷移時は前データをクリアして race を防ぐ
      if (cur.requestedId !== nextId) {
        state.current.data = null;
      }
      cur.requestedId = nextId;
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(fetchRepairById.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      // race: 古い応答は無視
      const requestedId = (state.current as any).requestedId;
      const payloadId = (action.payload as any)?.repair_id;
      if (requestedId && payloadId && payloadId !== requestedId) {
        return;
      }
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(fetchRepairById.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // updateRepair
    builder.addCase(updateRepair.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(updateRepair.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(updateRepair.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // updateRepairEstimateStatus
    builder.addCase(updateRepairEstimateStatus.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(updateRepairEstimateStatus.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(updateRepairEstimateStatus.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // analyzeRepairEstimates
    builder.addCase(analyzeRepairEstimates.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(analyzeRepairEstimates.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload?.repair || action.payload;
    });
    builder.addCase(analyzeRepairEstimates.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // deleteRepairEstimate
    builder.addCase(deleteRepairEstimate.pending, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(deleteRepairEstimate.fulfilled, (state, action) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(deleteRepairEstimate.rejected, (state) => {
      if (!state.current)
        state.current = { loading: false, error: false, data: null };
      state.current.loading = false;
      state.current.error = true;
    });

    // deleteRepair
    builder.addCase(deleteRepair.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(deleteRepair.fulfilled, (state, action) => {
      state.list.loading = false;
      // 削除された修繕をリストから除外
      const deletedRepairId = action.meta.arg.repair_id;
      state.list.data = state.list.data.filter(
        (repair: any) => repair.repair_id !== deletedRepairId
      );
    });
    builder.addCase(deleteRepair.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });
  },
});

export const { updateRepairStatusInList, updateEstimateInList } = repairsSlice.actions;
export default repairsSlice.reducer;
